import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { api } from '../lib/api';
import Button from '../components/Button';
import GuestAccountPrompt, { GuestAccountHint } from '../components/GuestAccountPrompt';
import { formatDateInTimezone, getBrowserTimezone } from '../utils/timezone';

interface Rental {
  _id: string;
  listingId: {
    _id: string;
    name: string;
    title: string;
    timezone: string;
    address?: string;
    supportPhoneNumber?: string;
  };
  startDateTime: string;
  endDateTime: string;
  displayEndDateTime: string;
  price: number;
  status: 'confirmed' | 'cancelled';
  paymentStatus: string;
}

interface EmailStatus {
  sent: number;
  failed: number;
  failedRentals: string[];
}

type StepStatus = 'complete' | 'warning' | 'error';

interface Step {
  id: string;
  title: string;
  description: string;
  status: StepStatus;
}

const PostCheckoutPage: React.FC = () => {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const orderId = searchParams.get('orderId');
  
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);
  const [guestAccountHint, setGuestAccountHint] = useState<GuestAccountHint | null>(null);

  useEffect(() => {
    if (!orderId) {
      setError('Invalid order ID');
      setIsLoading(false);
      return;
    }
    
    // Get email status / guest hint from navigation state if available
    const state = location.state as {
      emailStatus?: EmailStatus;
      rentalCount?: number;
      guestAccountHint?: GuestAccountHint;
      rentals?: Rental[];
    } | null;
    if (state?.emailStatus) {
      setEmailStatus(state.emailStatus);
    }
    if (state?.guestAccountHint) {
      setGuestAccountHint(state.guestAccountHint);
    }

    // A guest has no account for GET /rentals/my-rentals (authenticated-only)
    // to fetch from. The rentals the capture just created were handed to us
    // directly via navigation state from CheckoutPage.tsx — use those instead.
    if (!auth.isAuthenticated) {
      const confirmedRentals = (state?.rentals || []).filter(r => r.status === 'confirmed');
      setRentals(confirmedRentals.slice(0, 10));
      setIsLoading(false);
      return;
    }

    fetchRentals();
  }, [orderId, location.state, auth.isAuthenticated]);

  const fetchRentals = async () => {
    try {
      // Fetch recent rentals (the ones just created)
      const data = await api.get<{ rentals: Rental[] }>('/rentals/my-rentals');
      // Filter to only show confirmed rentals from recent order
      const confirmedRentals = data.rentals.filter(r => r.status === 'confirmed');
      setRentals(confirmedRentals.slice(0, 10)); // Show up to 10 most recent
    } catch {
      setError('Failed to load rental information');
    } finally {
      setIsLoading(false);
    }
  };

  const getSteps = (): Step[] => {
    const steps: Step[] = [
      {
        id: 'payment',
        title: 'Payment Successful',
        description: 'Your payment has been processed',
        status: 'complete',
      },
      {
        id: 'reservation',
        title: 'Reservations Booked',
        description: `${rentals.length} rental${rentals.length !== 1 ? 's' : ''} confirmed`,
        status: 'complete',
      },
    ];

    // Determine email step status
    let emailStepStatus: StepStatus = 'complete';
    let emailDescription = 'Confirmation email sent';
    
    if (emailStatus) {
      const totalRentals = emailStatus.sent + emailStatus.failed;
      if (emailStatus.failed === 0) {
        emailStepStatus = 'complete';
        if (totalRentals === 1) {
          emailDescription = 'Confirmation email sent';
        } else {
          emailDescription = `Confirmation emails sent for all ${totalRentals} reservations`;
        }
      } else if (emailStatus.sent > 0) {
        emailStepStatus = 'warning';
        emailDescription = `${emailStatus.sent} of ${totalRentals} confirmation emails sent`;
      } else {
        emailStepStatus = 'warning';
        emailDescription = 'Confirmation email delivery encountered issues';
      }
    }

    steps.push({
      id: 'email',
      title: 'Email Confirmation',
      description: emailDescription,
      status: emailStepStatus,
    });

    return steps;
  };

  const renderStepIcon = (status: StepStatus) => {
    if (status === 'complete') {
      return (
        <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    } else if (status === 'warning') {
      return (
        <div className="flex-shrink-0 w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading your order details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
          <h2 className="text-2xl font-bold text-red-800 mb-2">Error</h2>
          <p className="text-red-700">{error}</p>
          <Link to="/my-rentals" className="mt-4 inline-block">
            <Button>View My Rentals</Button>
          </Link>
        </div>
      </div>
    );
  }

  const steps = getSteps();

  return (
    <div className="max-w-4xl mx-auto">
      {/* Success Header */}
      <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg mb-8">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-10 w-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-4">
            <h1 className="text-3xl font-bold text-green-800">Order Complete!</h1>
            <p className="text-green-700 mt-1">
              Your payment has been processed successfully.
            </p>
          </div>
        </div>
      </div>

      {/* Guest post-purchase CTA */}
      {guestAccountHint && (
        <div className="mb-8">
          <GuestAccountPrompt hint={guestAccountHint} />
        </div>
      )}

      {/* Status Stepper */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Order Status</h2>
        <div className="space-y-6">
          {steps.map((step, index) => (
            <div key={step.id} className="relative">
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gray-200 -mb-6" style={{ height: '2.5rem' }} />
              )}
              
              {/* Step content */}
              <div className="flex items-start">
                {renderStepIcon(step.status)}
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Email warning details */}
        {emailStatus && emailStatus.failed > 0 && (
          <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Email Delivery Notice</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    {emailStatus.failed === 1 
                      ? 'Your confirmation email could not be delivered immediately.' 
                      : `${emailStatus.failed} confirmation emails could not be delivered immediately.`} 
                    {' '}Your reservation{emailStatus.failed + emailStatus.sent > 1 ? 's are' : ' is'} still confirmed, 
                    and we'll retry sending the email{emailStatus.failed > 1 ? 's' : ''}. You can view all your rental details below.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmed Rentals */}
      {rentals.length > 0 && (
        <>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Reservations</h2>
          <div className="space-y-4 mb-8">
            {rentals.map((rental) => {
              // A rental's start/end instant means whatever it means in the
              // listing's timezone, not the renter's browser timezone.
              const timezone = rental.listingId.timezone || getBrowserTimezone();

              return (
              <div key={rental._id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {rental.listingId.title}
                    </h3>
                    <div className="space-y-1 text-gray-600">
                      <p>
                        <span className="font-medium">Start:</span>{' '}
                        {formatDateInTimezone(new Date(rental.startDateTime), timezone)}
                      </p>
                      <p>
                        <span className="font-medium">End:</span>{' '}
                        {formatDateInTimezone(new Date(rental.displayEndDateTime), timezone)}
                      </p>
                      <p>
                        <span className="font-medium">Price:</span> ${rental.price.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                    Confirmed
                  </span>
                </div>

                {/* Contact Information */}
                {(rental.listingId.address || rental.listingId.supportPhoneNumber) && (
                  <div className="border-t pt-4 mt-4 bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Contact Information</h4>
                    <div className="space-y-1 text-blue-800">
                      {rental.listingId.address && (
                        <p>
                          <span className="font-medium">Address:</span>{' '}
                          {rental.listingId.address}
                        </p>
                      )}
                      {rental.listingId.supportPhoneNumber && (
                        <p>
                          <span className="font-medium">Support Phone:</span>{' '}
                          {rental.listingId.supportPhoneNumber}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 justify-center pb-8">
        <Link to="/my-rentals">
          <Button variant="secondary" size="lg">
            View All My Rentals
          </Button>
        </Link>
        <Link to="/">
          <Button size="lg">
            Browse More Listings
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default PostCheckoutPage;
